import { useRouter } from 'next/router'

import { getItineraryData } from '@/redux/slices/itinerary'
import { selectExperiment } from '@/redux/slices/statsig'
import { useAppSelector } from '@/redux/store/hooks'

import { PE_DOM_EXPERIMENT } from '@/constants/flights/premiumEconomy'
import useVariant from '@/hooks/statsig/useVariant'
import { isServer } from '@/utils/general'
import { getCookie } from '@/utils/general/cookie'

/**
 * PE discovery gate:
 *
 *   - International: always on, for every trip type — no Statsig wait.
 *   - Domestic: the `PE_DOM_DT` Statsig cohort, resolved in this precedence so homepage
 *     cabin pills, interstitial, and search `isPE` flags cannot diverge from
 *     `isPeDiscoveryVariantEnabled()`:
 *        a. Cookie override (`PE_DOM_DT=test|control`)
 *        b. SSR-seeded Redux Statsig value
 *        c. Client Statsig poll (only once resolved; defaults to control while loading)
 *
 * @param sectorOverride sector type to use instead of the page's own, for surfaces that
 * know it before a search exists (the home-page cabin pill).
 */
const usePremiumEconomyExperiment = (isIntl?: boolean) => {
  const router = useRouter()
  const pathname = router?.pathname || ''

  // Exact matches — `/flights/results/itinerary/loading` must not count as domestic SRP.
  const isIntlSrp = pathname === '/flights/international/results'
  const isDomSrp = pathname === '/flights/results'
  const isItinerary = !isIntlSrp && !isDomSrp && pathname.includes('/itinerary')
  const itineraryData = useAppSelector(getItineraryData)
  const itinerarySectorType = itineraryData?.SEARCH_DETAILS?.sectorType
  // Itinerary sector is unknown until the itinerary fetch lands — hold PE surfaces until then.
  const isItinerarySectorPending = isItinerary && !itinerarySectorType

  const { variant: clientVariant, loading } =
    useVariant({
      experiment: PE_DOM_EXPERIMENT.experimentName,
      cookieValue: PE_DOM_EXPERIMENT.cookieName,
      experimentParameter: PE_DOM_EXPERIMENT.experimentParameter,
      defaultVariant: PE_DOM_EXPERIMENT.controlVariant,
    }) || {}

  const reduxVariant = useAppSelector(selectExperiment(PE_DOM_EXPERIMENT.experimentName))

  const cookieVariant = isServer() ? '' : getCookie(PE_DOM_EXPERIMENT.cookieName)

  let variant: string = PE_DOM_EXPERIMENT.controlVariant
  if (cookieVariant) {
    variant = cookieVariant
  } else if (reduxVariant) {
    variant = reduxVariant
  } else if (!loading && clientVariant) {
    variant = clientVariant
  }

  const isDomTestVariant = variant === PE_DOM_EXPERIMENT.testVariant

  let isIntlSector = false
  if (isIntl !== undefined) {
    // Caller knows the live route (home / modify-search form) — trust it over the page.
    isIntlSector = isIntl
  } else if (isIntlSrp) {
    isIntlSector = true
  } else if (isItinerary) {
    isIntlSector = itinerarySectorType === 'INTL'
  }

  const variantEnabled = !isItinerarySectorPending && (isIntlSector || isDomTestVariant)

  return {
    variant,
    /**
     * Callers gate on `!loading && isPeTestVariant`. Intl is already a decision, so it
     * must not wait on the Statsig poll — otherwise a failed Statsig init leaves
     * `loading` true forever and suppresses intl PE.
     */
    loading: isItinerarySectorPending || (loading && !isIntlSector),
    /** `PE_DOM_DT` cohort alone. */
    variantEnabled,
    /** Combined render gate — the flag every PE surface should read. */
    isPeTestVariant: variantEnabled,
  }
}

export default usePremiumEconomyExperiment
